

import { useState, useEffect, useRef, useCallback } from 'react'

import { useShow } from '@/contexts/ShowContext'
import { useEpisode } from '@/contexts/EpisodeContext'
import { useModal } from '@/contexts/ModalContext'
import useAudioController from '@/hooks/useAudioController'
import useFileDownload from '@/hooks/useFileDownload'
import useKagamiBridge from '@/hooks/useKagamiBridge'
import Button from '@/components/ui/Button'
import { Button as ShadButton } from '@/components/ui/shadcn/button'
import VoiceSettingsModal from '@/components/modals/VoiceSettingsModal'
import { RiAiGenerateText } from 'react-icons/ri'
import Loader from '@/components/loader'
import { LuAudioLines } from 'react-icons/lu'
import { FiPause, FiPlay, FiChevronDown, FiAlertTriangle, FiDownload } from 'react-icons/fi'
import { FaUndo } from 'react-icons/fa'
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/shadcn/select'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from './ui/shadcn/dropdown-menu'


export default function ScriptEditor({ defaultValue, onChange, onSaveScript, onRefreshScript }) {
    const { show } = useShow()
    const { episode, fetchEpisode, updateScript } = useEpisode()
    const { openModal } = useModal()
    const { playAudio, stopAudio, isAudioPlaying, currentAudio } = useAudioController()
    const { downloadFile, downloadingFile } = useFileDownload()
    const { runTaskAndPoll, taskStatus } = useKagamiBridge()
    const [script, setScript] = useState(defaultValue || {})
    const [generating, setGenerating] = useState(false)
    const [updating, setUpdating] = useState(false)

    const saveTimeoutRef = useRef(null)

    const saveScript = useCallback(async (scriptObj, data, debounce = true) => {
        if (onSaveScript) {
            if (debounce) {
                if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
                saveTimeoutRef.current = setTimeout(() => onSaveScript(scriptObj, data), 2000)
            } else {
                if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
                await onSaveScript(scriptObj, data)
            }
        } else {
            updateScript(scriptObj, data, debounce)
        }
    }, [onSaveScript, updateScript])

    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
        }
    }, [])

    const refreshScript = async () => {
        if (onRefreshScript) {
            const updated = await onRefreshScript(script._id)
            if (updated) {
                setScript(updated)
                if (onChange) onChange(updated)
            }
        } else {
            await fetchEpisode()
        }
    }

    useEffect(() => {
        if (onRefreshScript) return
        if (!script?._id || !episode) return
        for (const seg of episode.segments || []) {
            for (const sc of seg.scenes || []) {
                const found = sc.script?.find(e => e._id === script._id)
                if (found) {
                    setScript(found)
                    if (onChange) onChange(found)
                    return
                }
            }
        }
    }, [episode])

    if (!defaultValue) {
        return (
            <div className='flex items-center justify-center h-full text-sm text-secondary-foreground/50'>
                No script element selected
            </div>
        )
    }

    const updateField = (key, value) => {
        const updated = { ...script, [key]: value }
        setScript(updated)
        if (onChange) onChange(updated)
        saveScript(script, { [key]: value })
    }

    const handleCharacterChange = (characterId) => {
        const character = characterId === 'no_character'
            ? null
            : show?.characters?.find(ch => ch._id === characterId) || null
        const updated = { ...script, character }
        setScript(updated)
        if (onChange) onChange(updated)
        saveScript(script, { character })
    }

    const handleAudioPreview = () => {
        if (isAudioPlaying && currentAudio === script.audio_url) {
            stopAudio()
        } else {
            playAudio(script.audio_url)
        }
    }

    const generateVoice = async (advanced) => {
        let voiceSettings = script.voice_settings || script.character?.voice_settings

        if (advanced) {
            const newSettings = await openModal(<VoiceSettingsModal voiceSettings={voiceSettings} />)
            if (!newSettings) return
            voiceSettings = newSettings
        }

        try {
            setGenerating(true)
            await runTaskAndPoll({
                function: 'generate-voice',
                showId: script.show || show?._id,
                episodeId: script.episode || episode?._id,
                segmentId: script.segment,
                sceneId: script.scene,
                payload: {
                    scriptId: script._id,
                    ...(voiceSettings ? { voiceSettings } : {})
                }
            })
            await refreshScript()
        } catch (error) {
            console.error(error)
        } finally {
            setGenerating(false)
        }
    }

    const handleEditScript = async(prompt) => {
        try{
            setGenerating(true)
            await runTaskAndPoll({
                function: 'edit-script-grok',
                showId: script.show || show?._id,
                payload: {
                    scriptId: script._id,
                    prompt:  prompt
                }
            })
            
            await refreshScript()
        }catch(error){
            console.log(error)
        }finally{
            setGenerating(false)
        }
    }

    const handleRevert = async () => {
        try{
            await saveScript(script, {
                speech: script.tts_speech,
                character: script.tts_character,
            }, false)
            await refreshScript()
        }catch(error){
            console.log(error)
        }
    }

    const hasVoiceId = script.character?.voice_ids?.find(c => c.language === 'en')?.voice_id
    const audioMismatch = (script.speech !== script.tts_speech || script.character?._id !== script.tts_character?._id) && script.audio_url

    return (
        <div className='flex flex-col gap-4 h-full overflow-y-auto relative px-4'>
            {/* Character */}
            <div className='flex flex-col gap-1'>
                <span className='text-[10px] text-secondary-foreground/80 px-1 select-none'>Character</span>
                <Select value={script.character?._id || 'no_character'} onValueChange={handleCharacterChange}>
                    <SelectTrigger>
                        <div className='flex items-center gap-2'>
                            {script.character?.thumbnail_url ? (
                                <img
                                    src={script.character.thumbnail_url}
                                    alt={script.character.name || ''}
                                    width={20}
                                    height={20}
                                    className='w-5 h-5 rounded-full object-cover flex-none'
                                />
                            ) : script.character ? (
                                <div className='w-5 h-5 rounded-full bg-secondary flex items-center justify-center flex-none'>
                                    <span className='text-[9px] text-secondary-foreground/60'>
                                        {script.character.name?.charAt(0)?.toUpperCase() || '?'}
                                    </span>
                                </div>
                            ) : null}
                            <SelectValue placeholder='Select character' />
                        </div>
                    </SelectTrigger>
                    <SelectContent className='bg-background'>
                        <SelectGroup>
                            <SelectItem value='no_character'>No Character</SelectItem>
                            {show?.characters?.map(char => (
                                <SelectItem key={char._id} value={char._id}>{char.name}</SelectItem>
                            ))}
                        </SelectGroup>
                    </SelectContent>
                </Select>
            </div>

            {/* Description */}
            <div className='flex flex-col gap-1'>
                <span className='text-[10px] text-secondary-foreground/80 px-1 select-none'>Description</span>
                    <textarea
                        value={script.description || ''}
                        onChange={e => updateField('description', e.target.value)}
                        placeholder='Scene description...'
                        className='placeholder:text-secondary-foreground/30 border border-secondary-foreground/50 min-h-30 bg-input w-full text-secondary-foreground rounded-lg text-xs/5 font-mono p-2 outline-hidden overflow-y-auto'
                        rows={3}
                    />
                
            </div>

            {/* Speech */}
            <div className='flex flex-col gap-1'>
                <div className='flex items-center justify-between gap-2'>
                    <span className='text-[10px] text-secondary-foreground/80 px-1 select-none'>Speech</span>
                    <DropdownMenu>
                        <DropdownMenuTrigger>
                            <div className='bg-secondary text-secondary-foreground border shadow hover:bg-secondary/80 p-1 text-[10px] rounded-sm'>
                                <RiAiGenerateText className='w-4 h-4' />
                            </div>
                            
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => handleEditScript('Rephrase the script, but keep it roughly at the same length and keep the same meaning')}>Rephrase</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleEditScript('Make the script text longer, but keep the same meaning')}>Longer</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditScript('Make the script text shorter, but keep the same meaning')}>Shorten</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditScript('Summarize the script text, but keep the same meaning')}>Summarize</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleEditScript('Make the script text more dramatic')}>Dramatic</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditScript('Make the script text more emotional')}>Emotional</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditScript('Make the script text more exciting')}>Exciting</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditScript('Make the script text more engaging')}>Engaging</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                
                <div className='flex flex-col relative'>
                    <textarea
                        value={script.speech || ''}
                        onChange={e => updateField('speech', e.target.value)}
                        placeholder='Character dialogue...'
                        className='placeholder:text-secondary-foreground/30 border border-secondary-foreground/50 min-h-30 bg-input w-full text-secondary-foreground rounded-lg text-xs/5 font-mono p-2 outline-hidden overflow-y-auto'
                        rows={6}
                    />
                    {audioMismatch && (
                        <div className='flex items-center gap-1 text-[10px] text-orange-400 px-2 pb-1 absolute bottom-0 right-1 pointer-events-none'>
                            <FiAlertTriangle className='w-3 h-3' />
                            <span>Voice and script mismatch</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Audio Controls */}
            {script.character?.voice_ids?.find(c => c.language === 'en')?.voice_id && <div className='flex flex-col gap-1'>
                <span className='text-[10px] text-secondary-foreground/80 px-1 select-none'>Audio</span>
                <div className='flex items-center gap-2'>
                    <Button
                        disabled={!script.audio_url}
                        type='secondary'
                        className='w-full'
                        icon={isAudioPlaying && currentAudio === script.audio_url ? <FiPause className='w-4 h-4' /> : <FiPlay className='w-4 h-4' />}
                        onClick={handleAudioPreview}
                    >
                        Preview voice
                    </Button>
                    {audioMismatch && <Button type='secondary' icon={<FaUndo className='w-3 h-3' />} onClick={handleRevert}>Revert</Button>}
                    <div className='flex shrink-0'>
                        <Button
                            type='secondary'
                            className='rounded-r-none'
                            disabled={!hasVoiceId || generating}
                            icon={<LuAudioLines className='w-4 h-4' />}
                            onClick={() => generateVoice(false)}
                        >
                            Generate voice
                        </Button>
                        
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <ShadButton
                                    variant='secondary'
                                    disabled={!hasVoiceId || generating}
                                    className='rounded-l-none border-l-none border bg-secondary h-full px-1 py-2'
                                >
                                    <FiChevronDown className='w-4 h-4' />
                                </ShadButton>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => generateVoice(true)}>Advanced Settings</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                    {script.audio_url && (
                        <Button
                            type='secondary'
                            disabled={downloadingFile}
                            icon={<FiDownload className='w-4 h-4' />}
                            onClick={() => downloadFile(script.audio_url, `${script.character?.name || 'voice'}-${script._id}`)}
                        />
                    )}
                </div>
                <div className='flex items-center gap-1'>
                    {script.audio_duration !== null && <span className='text-[10px] text-secondary-foreground/80 bg-secondary/60 rounded-lg px-2 py-1'> Duration: {(script.audio_duration / 1000).toFixed(1)}s</span>}
                    {script.voice_settings?.model && <span className='text-[10px] text-secondary-foreground/80 bg-secondary/60 rounded-lg px-2 py-1'> Model: {script.voice_settings.model || 'eleven_multilingual_v2'}</span>}
                    {!Number.isNaN(script.voice_settings?.speed || NaN) && <span className='text-[10px] text-secondary-foreground/80 bg-secondary/60 rounded-lg px-2 py-1'> Speed: {script.voice_settings.speed}x</span>}
                    {!Number.isNaN(script.voice_settings?.stability || NaN) && <span className='text-[10px] text-secondary-foreground/80 bg-secondary/60 rounded-lg px-2 py-1'> Stability: {script.voice_settings.stability*100}%</span>}
                    {!Number.isNaN(script.voice_settings?.similarityBoost || NaN) && <span className='text-[10px] text-secondary-foreground/80 bg-secondary/60 rounded-lg px-2 py-1'> Similarity: {script.voice_settings.similarityBoost*100}%</span>}
                    {!Number.isNaN(script.voice_settings?.style || NaN) && <span className='text-[10px] text-secondary-foreground/80 bg-secondary/60 rounded-lg px-2 py-1'> Style: {script.voice_settings.style*100}%</span>}
                </div>
            </div>}

            {generating && <Loader status={taskStatus} />}
        </div>
    )
}
